import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import FormSubmitButton from "Common/Custom/FormSubmitButton";
import { HeadingOne } from "Common/Custom/HeadingOne";
import PageBody from "Common/Custom/PageBody";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormRenderer from "Common/Custom/FormRenderer";
import Card from "../../../Components/CardStucher";
import AddRoomBtn from "../../../Components/AddRoomBtn";
import ActionDropdown from "../../../Components/ActionDropdown";
import DeleteButton from "../../../Components/DeleteButton";
import CustomerRadio from "../../../Components/CustomerRadio";
import CustomerFormModal from "../../../Customer/CreateNewCustomerForm";
import useRoomChange from "../../../Components/useRoomChange";
import useBillingCalculation from "../../../Components/useBillingCalculation";
import useRoomTypeOptions from "../../../Components/useRoomTypeOptions";
import ExtraFacilitySection from "../../../Components/ExtraFacilitySection";

import {
    getBookingRef,
    getCombinedRoomInfo
} from "adminApi/RoomBookingApi/RoomBooking";

import {
    fetchOptions,
    RoomFieldsOpction,
    deleteCustomerHelper,
    enrichRoom,
    // disableFields
} from "../../../HELPERS/Helpers";

const RoomBookingForm = ({
    values = {},
    errors = {},
    handleChange = () => { },
    handleSubmit = () => { },
    loading = false,
    setValues = () => { },
    bookingTypesOptions = [],
    roomTypesOption = [],
    paymentMethodTypesOption = [],
    taxConfig = {},
    initialValues = {},
    form_data = {},
    ChildrenMinAge = null,
    ChildrenMaxAge = null,
    roomFields = [],
    setChildrenMinAge = () => { },
    setChildrenMaxAge = () => { },
}) => {
    // Customer modal
    const rootContainer = document.getElementById("root-container");

    const [show, setShow] = useState(false);
    const [title, setTitle] = useState("");
    const [customerType, setCustomerType] = useState("");
    const [selectedCustomerId, setSelectedCustomerId] = useState("");
    const [selectedCustomer, setSelectedCustomer] = useState({});
    const [newCustomer, setNewCustomer] = useState({ name: "", phone: "", email: "" });
    const [existingCustomers, setExistingCustomers] = useState([]);
    const [bookingReferencesOption, setBookingReferencesOption] = useState([]);
    const [rooms, setRooms] = useState([]);
    const [roomNumbersByType, setRoomNumbersByType] = useState({});
    const [complementryByType, setComplementryByType] = useState({});
    const [roomOfferByType, setRoomOfferByType] = useState({});
    const [roomTypeInfoByType, setRoomTypeInfoByType] = useState({});
    const [commissionRate, setCommissionRate] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(0);

    const isInitializedRef = useRef(false);
    const roomsLoadedRef = useRef(false);
    const previousRoomsRef = useRef();
    const customerAutoSelectedRef = useRef(false);

    const PreveBookedRoomIds = rooms?.map(r => r.book_details_id).filter(Boolean) || [];

    // Room Type Options Hook
    const { ensureTypeOptions } = useRoomTypeOptions({
        roomNumbersByType,
        setRoomNumbersByType,
        setRoomOfferByType,
        setComplementryByType,
        setRoomTypeInfoByType,
        roomTypeInfoByType,
        roomOfferByType,
        values,
        setChildrenMinAge,
        setChildrenMaxAge,
        getCombinedRoomInfo
    });


    // Update Room
    const updateRoom = useCallback((index, patch) => {
        setRooms((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    }, []);

    // New Customer Modal
    useEffect(() => {
        if (!newCustomer?.customer_id) {
            return
        }
        setExistingCustomers((prev) => {
            return [...prev, newCustomer];
        });
        setShow(false)
    },
        [
            newCustomer,
            setNewCustomer
        ]
    );

    // Set Rooms — wait for roomTypesOption to enrich bed/person charges before recalculating
    useEffect(() => {
        if (values.rooms && values.rooms.length > 0 && !isInitializedRef.current && roomTypesOption.length > 0) {
            const enriched = values.rooms.map(room => {
                const typeDetails = roomTypeInfoByType[room.room_id] || roomTypesOption.find(rt => String(rt.room_id) === String(room.room_id));
                const roomOffer = roomOfferByType[room.room_id];
                return enrichRoom(room, typeDetails, roomOffer, values);
            });
            setRooms(enriched);
            isInitializedRef.current = true;
        }
    },
        [
            values.rooms,
            roomTypesOption,
            roomTypeInfoByType,
            roomOfferByType,
            enrichRoom
        ]
    );

    // Set Existing Customers
    // Bug Fix 1: customer_phone can be null — removed it from the truthy gate
    // so the customer row is always populated when customer_id + customer_name exist
    useEffect(() => {
        if (
            values.customer_id &&
            values.customer_name &&
            existingCustomers.length === 0
        ) {
            const id = String(values.customer_id);
            const cust = {
                customer_id: id,
                full_name: values.customer_name,
                phone: values.customer_phone || "-",
            };
            setExistingCustomers([cust]);
            setSelectedCustomerId(id);
        }
    },
        [
            values.customer_id,
            values.customer_name,
            values.customer_phone,
            existingCustomers.length,
            setExistingCustomers
        ]
    );

    // Set Room Types Options
    useEffect(() => {
        if (roomTypesOption.length > 0 && rooms.length > 0 && !roomsLoadedRef.current) {
            const loadRoomDetails = async () => {
                for (const room of rooms) {
                    if (room.room_id) {
                        await ensureTypeOptions(room.room_id, room, rooms.indexOf(room));
                    }
                }
                roomsLoadedRef.current = true;
            };
            loadRoomDetails();
        }
    },
        [
            roomTypesOption.length,
            rooms.length,
            ensureTypeOptions
        ]
    );

    // Re-enrich rooms after roomTypeInfoByType is populated
    useEffect(() => {
        if (roomsLoadedRef.current && rooms.length > 0 && Object.keys(roomTypeInfoByType).length > 0) {
            const enriched = rooms.map(room => {
                const typeDetails = roomTypeInfoByType[room.room_id];
                const roomOffer = roomOfferByType[room.room_id];
                return enrichRoom(room, typeDetails, roomOffer, values);
            });
            setRooms(enriched);
        }
    },
        [
            roomTypeInfoByType,
            rooms.length,
            roomOfferByType,
            enrichRoom
        ]
    );

    // Set new Rooms
    useEffect(() => {
        if (rooms.length > 0 && isInitializedRef.current) {
            const currentRoomsString = JSON.stringify(rooms);
            const previousRoomsString = previousRoomsRef.current;

            if (currentRoomsString !== previousRoomsString) {
                previousRoomsRef.current = currentRoomsString;

                setValues((prev) => {
                    if (JSON.stringify(prev.rooms) !== currentRoomsString) {
                        return { ...prev, rooms };
                    }
                    return prev;
                });
            }
        }
    },
        [
            rooms,
            setValues
        ]
    );

    //get booking references
    useEffect(() => {
        if (values.booking_type) {
            fetchOptions(getBookingRef, values.booking_type, setBookingReferencesOption);
        }
    },
        [
            values.booking_type,
            fetchOptions
        ]
    );

    // Set Commission Rate — updates whenever booking reference changes
    useEffect(() => {
        if (bookingReferencesOption.length > 0 && values.booking_reference) {
            const sel = bookingReferencesOption.find((ele) => String(ele.book_com_id) === String(values.booking_reference));
            if (sel && sel.commission_rate) {
                setCommissionRate(sel.commission_rate);
                setValues((prev) => ({ ...prev, commission_percent: String(sel.commission_rate) }));
            }
        }
    },
        [
            values.booking_reference,
            bookingReferencesOption,
            setValues
        ]
    );

    // Billing Calculation Hook
    const { billingRates, computedRooms } = useBillingCalculation({ rooms, values, taxConfig, setValues });


    // Auto select customer
    useEffect(() => {
        if (!values.customer_id && existingCustomers.length === 1 && !customerAutoSelectedRef.current) {
            const id = String(existingCustomers[0].customer_id);
            setSelectedCustomerId(id);
            setValues((prev) => ({ ...prev, customer_id: id }));
            customerAutoSelectedRef.current = true;
        }
    },
        [
            existingCustomers,
            values.customer_id,
            setValues,
            newCustomer
        ]);

    // Handle Room Change
    // Handle Room Change
    const { handleRoomChange } = useRoomChange({
        rooms,
        roomFields: roomFields,     // ← JSON drives the empty shape
        roomTypesOption,
        roomTypeInfoByType,
        roomOfferByType,
        complementryByType,
        values,
        updateRoom,
        ensureTypeOptions,
    });

    // Handle Room Field Event
    const onRoomFieldEvent = useCallback((index) => (e) => {
        const { name, value } = e.target || {};
        handleRoomChange(index, name, value);
    },
        [
            handleRoomChange
        ]
    );

    // Handle Customer Type Change
    const handleExited = () => {
        setCustomerType("");
        setSelectedCustomerId("");
        setNewCustomer([]);
    };

    const selectedRoomIds = useMemo(() => rooms.map(r => r.room_no).filter(Boolean), [rooms]);

    const addRoom = useCallback(() => setRooms((prev) => [...prev, initialValues.rooms[0]]), []);

    const removeRoom = useCallback((index) => setRooms((prev) => {
        return prev.length > 1 ? prev.filter((_, i) => i !== index) : prev;
    }), []);

    // destructure form data
    const {
        Reservation_Details,
        RoomFields,
        Payment_Details,
        Advance_Details,
        extraFields,
        Rent_Info,
    } = form_data



    const optionConfig = {
        booking_type: {
            list: bookingTypesOptions,
            valueKey: "booking_type_id",
            labelKey: "booking_type_name"
        },
        booking_reference: {
            list: bookingReferencesOption,
            valueKey: "book_com_id",
            labelKey: "booking_source_name"
        }
    };

    const reservationDetails = Reservation_Details.map((item) => {
        if (item.name === "checkin_date") {
            return {
                ...item,
                disablePast: true,
            };
        }

        if (item.name === "checkout_date") {
            return {
                ...item,
                minDate: values.checkin_date || null,
                disablePast: true,
            };
        }

        return item;
    });


    return (
        <React.Fragment>
            <PageBody
                card={false}
                onSubmit={(e) => { handleSubmit(e); setIsSubmitted(isSubmitted + 1) }}
                headerchildren={
                    <HeadingOne>
                        Create Room Booking
                    </HeadingOne>
                }
            >
                {/* Reservation Details */}
                <Card heading="Reservation Details">
                    <FormRenderer
                        fields={
                            reservationDetails
                        }
                        values={values}
                        errors={errors}
                        handleChange={handleChange}
                        optionConfig={optionConfig}
                    />
                </Card>

                {/* Room Details */}
                <Card heading="Room Details">
                    <div className="col-12 room-table">
                        {rooms.map((room, index) => (
                            <React.Fragment key={index}>
                                {rooms.length > 1 && (
                                    <div className="border border-dashed py-1 text-muted fw-bold text-center">
                                        <span className="opacity-25 fs-3">Room {index + 1}</span>
                                    </div>
                                )}
                                <div className="table-responsive">
                                    <table className="table mb-0 border">
                                        <thead>
                                            <tr>
                                                <th className="p-0">
                                                    <div className="px-3 py-2 fw-semibold">Room Info</div>
                                                </th>
                                                <th className="p-0"></th>
                                                <th className="p-0">
                                                    <div className="border-start px-3 py-2 fw-semibold">Action</div>
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr>
                                                <td className="align-top border-end border-bottom-0" colSpan={2}>
                                                    <div className="row g-3">
                                                        {RoomFields.map((field, idx) => {
                                                            if (field.type === "select") {

                                                                return (
                                                                    <FormSelect
                                                                        key={idx}
                                                                        field={field}
                                                                        value={room[field.name]}
                                                                        disabled={
                                                                            (field.name === "room_id")
                                                                                ? PreveBookedRoomIds.includes(room.book_details_id)
                                                                                : false
                                                                        }
                                                                        options={RoomFieldsOpction(field.name, room, roomTypesOption, roomNumbersByType, selectedRoomIds)}
                                                                        handleChange={onRoomFieldEvent(index)}
                                                                        error={
                                                                            (!room[field.name] && isSubmitted > 0 && field.required) ? `${field.label} is required` : ''
                                                                        }
                                                                    />
                                                                );
                                                            }
                                                            return (
                                                                <FormInput
                                                                    key={idx}
                                                                    field={{
                                                                        ...field,
                                                                        label: field.name === "children" ? `Children (Age Below ${ChildrenMinAge})` : field.label,
                                                                        disabled: !room.room_id || !room.room_no,
                                                                        // max: field.name === "no_of_people" ? room.capacity : null,
                                                                        // maxLength: field.name === "no_of_people" ? room.capacity : null
                                                                    }}
                                                                    value={room[field.name]}
                                                                    handleChange={onRoomFieldEvent(index)}
                                                                    error={
                                                                        (!room[field.name] && isSubmitted > 0 && field.required)
                                                                            ? `${field.label} is required`
                                                                            : (field.name === "no_of_people" && room?.no_of_people > room?.capacity) ? `Adults Maximum Limit ${room.capacity}` : ''
                                                                    }
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                </td>
                                                <td className="align-bottom text-center col-action" style={{ width: "1%" }}>
                                                    {index === rooms.length - 1 && (
                                                        <AddRoomBtn
                                                            values={values}
                                                            onClick={addRoom}
                                                            rooms={rooms}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                            <tr className="border-bottom">
                                                <td className="align-top p-0 border-bottom-0 border-end col-left" style={{ width: "70%" }}>
                                                    <div className="border-top">
                                                        <div className="d-flex align-items-center border-bottom px-3 py-2 justify-content-between">
                                                            <div className="fw-semibold">Customer Info</div>
                                                            {index === 0 && (
                                                                <ActionDropdown
                                                                    setCustomerType={setCustomerType}
                                                                    setTitle={setTitle}
                                                                    setShow={setShow}
                                                                />
                                                            )}
                                                        </div>

                                                        <div className="table-responsive">
                                                            <table className="table table-sm mb-0">
                                                                <thead className="bg-light">
                                                                    <tr>
                                                                        <th width="60" className="text-center">SL</th>
                                                                        <th>Name</th>
                                                                        <th>Mobile No</th>
                                                                        <th width="80">Action</th>
                                                                    </tr>
                                                                </thead>

                                                                <tbody>
                                                                    {existingCustomers?.length ? (
                                                                        existingCustomers.map((customer, i) => (
                                                                            <tr key={i}>
                                                                                <td className="text-center align-middle">
                                                                                    <CustomerRadio
                                                                                        name="customer_id"
                                                                                        value={customer.customer_id}
                                                                                        selectedValue={values.customer_id}
                                                                                        onChange={(id) => {
                                                                                            setSelectedCustomerId(id);
                                                                                            setValues(prev => ({ ...prev, customer_id: id }));
                                                                                        }}
                                                                                    />
                                                                                </td>
                                                                                <td className="align-middle">{customer.full_name || customer.name || "-"}</td>
                                                                                <td className="align-middle">{customer.phone}</td>
                                                                                <td className="text-center align-middle">
                                                                                    <DeleteButton
                                                                                        disabled={existingCustomers.length === 1}
                                                                                        onClick={() => deleteCustomerHelper({
                                                                                            customerId: customer.customer_id,
                                                                                            setExistingCustomers,
                                                                                            setValues,
                                                                                            selectedCustomerId,
                                                                                            setSelectedCustomerId
                                                                                        })}
                                                                                    />
                                                                                </td>
                                                                            </tr>
                                                                        ))
                                                                    ) : (
                                                                        <tr>
                                                                            <td colSpan={4} className="text-center align-middle text-muted">
                                                                                Select Customer Details
                                                                            </td>
                                                                        </tr>
                                                                    )}
                                                                    <tr key={index}>
                                                                        <ExtraFacilitySection
                                                                            extraFields={extraFields}
                                                                            room={room}
                                                                            values={values}
                                                                            errors={errors}
                                                                            index={index}
                                                                            onRoomFieldEvent={onRoomFieldEvent}
                                                                            ChildrenMinAge={ChildrenMinAge}
                                                                            ChildrenMaxAge={ChildrenMaxAge}
                                                                        />
                                                                    </tr>
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="align-top p-0 border-bottom-0 border-top col-right" style={{ width: "30%" }}>
                                                    <div className="d-flex align-items-center border-bottom px-3 py-2 mt-1">
                                                        <div className="fw-semibold">Room Rent Info</div>
                                                    </div>
                                                    <div className="row g-2 small p-3">
                                                        {Rent_Info.map((field, idx) => {
                                                            // room_offer and room_total are displayed in the billing summary below
                                                            if (field.name === "room_offer" || field.name === "room_total" || field.name === "complementary_price") return null;

                                                            if (field.type === "select") {
                                                                let roomKey = ""
                                                                const { room_id, room_no } = room
                                                                if (room_id && room_no) {
                                                                    roomKey = `${room_id}_${index}`;
                                                                }

                                                                return (
                                                                    <FormSelect
                                                                        key={idx}
                                                                        field={field}
                                                                        value={room?.[field.name]}
                                                                        disabled={!room?.room_id}
                                                                        options={
                                                                            (complementryByType[roomKey] || [])?.map((c) => ({
                                                                                value: c.complementary_id,
                                                                                label: c.complementary_name,
                                                                            }))
                                                                        }
                                                                        handleChange={onRoomFieldEvent(index)}
                                                                        error={errors?.rooms?.[index]?.[field.name] || errors[field.name]}
                                                                    />
                                                                );
                                                            }

                                                            let IsDesable = false;
                                                            if (field.name === "room_rate") {
                                                                IsDesable = !room?.room_id || !room?.room_no;
                                                            } else if (field.name === "noofroom") {
                                                                IsDesable = true;
                                                            }
                                                            return (
                                                                <FormInput
                                                                    key={idx}
                                                                    field={{ ...field, disabled: IsDesable }}
                                                                    value={field.name !== "noofroom" ? (room?.[field.name] || "") : (rooms?.length || 0)}
                                                                    handleChange={onRoomFieldEvent(index)}
                                                                    error={errors?.rooms?.[index]?.[field.name] || errors[field.name]}
                                                                />
                                                            );
                                                        })}

                                                        {/* Billing Summary */}
                                                        {room?.room_id && room?.room_no && (
                                                            <div className="col-12 mt-2">
                                                                <table className="table table-sm table-borderless mb-0">
                                                                    <tbody>
                                                                        <tr>
                                                                            <td className="ps-0 text-muted">Room Rate ({Number(computedRooms[index].room_rate || 0).toFixed(2)} * {Number(computedRooms[index].facilityDays || 0)})</td>
                                                                            <td className="pe-0 text-end">
                                                                                {Number(computedRooms[index].room_rate_totle || 0).toFixed(2)}
                                                                            </td>
                                                                        </tr>

                                                                        {Number(computedRooms[index].extra_room_total || 0) > 0 && (
                                                                            <tr>
                                                                                <td className="ps-0 text-muted">
                                                                                    {(() => {
                                                                                        const from = room.extracheck_in_date || values.checkin_date;
                                                                                        const to = room.extracheck_out_date || values.checkout_date;
                                                                                        let nights = 1;
                                                                                        if (from && to) {
                                                                                            const diff = Math.ceil((new Date(to) - new Date(from)) / (1000 * 60 * 60 * 24));
                                                                                            nights = diff > 0 ? diff : 1;
                                                                                        }
                                                                                        return `Extra Facility Charges (${nights} Night${nights !== 1 ? "s" : ""})`;
                                                                                    })()}
                                                                                </td>
                                                                                <td className="pe-0 text-end">
                                                                                    {Number(computedRooms[index].extra_room_total || 0).toFixed(2)}
                                                                                </td>
                                                                            </tr>
                                                                        )}

                                                                        {Number(room.complementary_price || 0) > 0 && (
                                                                            <tr>
                                                                                <td className="ps-0 text-muted">
                                                                                    {
                                                                                        room.complementary_price_type === 'DAY_WISE' ? `${room.complementary_name} (${computedRooms[index].complementary_price} * ${computedRooms[index].facilityDays})` : room.complementary_name
                                                                                    }
                                                                                </td>
                                                                                <td className="pe-0 text-end">
                                                                                    {
                                                                                        Number(computedRooms[index].complementary_total || 0).toFixed(2)
                                                                                    }
                                                                                </td>
                                                                            </tr>
                                                                        )}

                                                                        {
                                                                            Number(computedRooms[index].room_offer || 0) > 0 && (() => {

                                                                                return (
                                                                                    <tr className="text-success">
                                                                                        <td className="ps-0">
                                                                                            Room Offer ({Number(computedRooms[index].room_offer || 0)}%)
                                                                                        </td>
                                                                                        <td className="pe-0 text-end">
                                                                                            − {computedRooms[index].room_offer_amt ? Number(computedRooms[index].room_offer_amt).toFixed(2) : "0.00"}
                                                                                        </td>
                                                                                    </tr>
                                                                                );
                                                                            })()}

                                                                        <tr className="border-top fw-semibold">
                                                                            <td className="ps-0">Room Total</td>
                                                                            <td className="pe-0 text-end">
                                                                                {Number(computedRooms[index].room_total || 0).toFixed(2)}
                                                                            </td>
                                                                        </tr>
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                <td className="text-center border border-end-0 col-action" style={{ width: "1%" }}>
                                                    {rooms.length > 1 && (
                                                        <DeleteButton
                                                            disabled={false}
                                                            onClick={() => removeRoom(index)}
                                                        />
                                                    )}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </Card>

                {/* Payments + Billing + Advance */}
                <Card>
                    <div className="row g-3 row-deck">
                        <div className="col-md-6 col-xxl-4">
                            <Card
                                heading={
                                    <h6 className="card-title">Payments Details</h6>
                                }
                                CardHeaderClass="card-header"
                                CardRowClass="row g-3"
                            >
                                <FormRenderer
                                    fields={Payment_Details}
                                    values={values}
                                    errors={errors}
                                    handleChange={handleChange}
                                    extraValues={{
                                        commission_amount: billingRates.commission_amount,
                                        commission_percent: commissionRate
                                    }}
                                />

                            </Card>
                        </div>

                        <div className="col-md-6 col-xxl-4">
                            <Card
                                heading={
                                    <h6 className="card-title">Billing Details</h6>
                                }
                                CardHeaderClass="card-header"
                                CardRowClass="row g-3"
                            >
                                <table className="table table-borderless mb-0">
                                    <tbody>
                                        <tr>
                                            <td className="ps-0">Booking Charge</td>
                                            <td className="pe-0">{
                                                (billingRates?.RoomBookingCharge)?.toFixed(2)
                                            }
                                            </td>
                                        </tr>
                                        <tr>
                                            <td className="ps-0">
                                                Tax
                                                ({billingRates.total_tax_rate} %)
                                            </td>
                                            <td className="pe-0">{
                                                (billingRates.tax || 0).toFixed(2)
                                            }</td>
                                        </tr>
                                        <tr>
                                            <td className="ps-0">
                                                Service Charge
                                                ({billingRates.service_charge} %)

                                            </td>
                                            <td className="pe-0">{
                                                (billingRates.serviceCharge || 0).toFixed(2)
                                            }</td>
                                        </tr>
                                        {billingRates.discountAmount > 0 && (
                                            <tr>
                                                <td className="ps-0">
                                                    Discount ({values.discount} %)
                                                </td>
                                                <td className="pe-0">-{
                                                    (billingRates.discountAmount).toFixed(2)
                                                }</td>
                                            </tr>
                                        )

                                        }
                                        <tr className="fw-bold">
                                            <td className="ps-0">Total</td>
                                            <td className="pe-0">{(billingRates.total || 0).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </Card>
                        </div>
                        <div className="col-md-6 col-xxl-4">
                            <Card
                                heading={
                                    <h6 className="card-title">Advance Details</h6>
                                }
                                CardHeaderClass="card-header"
                                CardRowClass="row g-3"
                            >

                                <FormRenderer
                                    fields={
                                        Advance_Details?.map((e) => {
                                            if (e.name !== "remaining_amount") {
                                                return {
                                                    ...e
                                                };
                                            }
                                        })
                                    }
                                    values={values}
                                    errors={errors}
                                    handleChange={handleChange}
                                    optionConfig={{
                                        payment_method: {
                                            list: paymentMethodTypesOption,
                                            valueKey: "payment_method_id",
                                            labelKey: "payment_method_name"
                                        }
                                    }}
                                />
                            </Card>
                        </div>
                    </div>

                </Card>

                <FormSubmitButton
                    label={
                        "Submit"
                    }
                    loading={loading}
                    colSize="col-12"
                    align="end"
                />
            </PageBody>

            {/* Customer Modal */}
            <  CustomerFormModal
                show={show}
                setShow={setShow}
                handleExited={handleExited}
                title={title}
                customerType={customerType}
                rootContainer={rootContainer}
                selectedCustomerId={selectedCustomerId}
                setSelectedCustomerId={setSelectedCustomerId}
                setSelectedCustomer={setSelectedCustomer}
                setNewCustomer={setNewCustomer}
                setValues={setValues}
                selectedCustomer={selectedCustomer}
                newCustomer={newCustomer}
                setExistingCustomers={setExistingCustomers}
            />
        </React.Fragment >
    );
};




export default RoomBookingForm;